import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Download, FileText, Clock, ShieldCheck, AlertTriangle, Eye, Lock } from 'lucide-react';
import PDFViewer from '../components/PDFViewer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SharedDocument = () => {
  // Token comes from the query string (?token=) so the long JWT never sits in a
  // URL path segment — IIS/http.sys rejects path segments >260 chars with a 400.
  // Fall back to the legacy /shared/:token path param for older links.
  const params = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || params.token;
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // In-page preview state. We render the file content directly (no iframe) so the
  // app's strict framing CSP (frame-ancestors 'none' / X-Frame-Options: DENY) can't
  // block it — an iframe pointing at our own origin was being refused.
  const [previewKind, setPreviewKind] = useState(null); // 'pdf'|'image'|'text'|'unsupported'|'error'
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewText, setPreviewText] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    document.title = 'ReasonDMS · Shared Document';
    fetchShared();
    // eslint-disable-next-line
  }, [token]);

  const fetchShared = async (pwd) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/sharing/shared`, {
        params: { token, ...(pwd ? { password: pwd } : {}) }
      });
      setData(res.data);
      setError(null);
      setNeedsPassword(false);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || 'Failed to load shared document';
      if (status === 401 && (detail || '').toLowerCase().includes('password')) {
        setNeedsPassword(true);
        if (pwd) setError('Incorrect password');
      } else {
        setError(detail);
      }
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const onPasswordSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    fetchShared(password);
  };

  const handleDownload = () => {
    const url = `${API_URL}/api/sharing/shared/download?token=${encodeURIComponent(token)}${password ? `&password=${encodeURIComponent(password)}` : ''}`;
    window.location.href = url;
  };

  // Fetch the file bytes once validated and render them in-page (no iframe).
  useEffect(() => {
    if (!data) return;
    let objectUrl = null;
    let cancelled = false;
    const loadPreview = async () => {
      setPreviewLoading(true);
      try {
        const url = `${API_URL}/api/sharing/shared/download?token=${encodeURIComponent(token)}${password ? `&password=${encodeURIComponent(password)}` : ''}`;
        const res = await axios.get(url, { responseType: 'blob' });
        if (cancelled) return;
        const blob = res.data;
        const ct = (blob.type || res.headers['content-type'] || '').toLowerCase();
        const key = (data.document?.s3_key || data.document?.title || '').toLowerCase();
        const isOffice = /\.(docx?|xlsx?|pptx?|odt|ods|odp|rtf)$/.test(key)
          || ct.includes('word') || ct.includes('officedocument')
          || ct.includes('spreadsheet') || ct.includes('ms-excel') || ct.includes('presentation');
        let k = 'unsupported';
        if (isOffice) k = 'unsupported';
        else if (ct.includes('pdf') || key.endsWith('.pdf')) k = 'pdf';
        else if (ct.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|tiff?|svg)$/.test(key)) k = 'image';
        else if (ct.startsWith('text/') || ct.includes('json') || ct.includes('xml') || ct.includes('csv')
                 || /\.(txt|md|csv|json|xml|log|html?)$/.test(key)) k = 'text';

        if (k === 'text') {
          const t = await blob.text();
          if (!cancelled) setPreviewText(t);
        } else if (k === 'pdf' || k === 'image') {
          objectUrl = URL.createObjectURL(blob);
          if (!cancelled) setPreviewUrl(objectUrl);
        }
        if (!cancelled) setPreviewKind(k);
      } catch (e) {
        if (!cancelled) setPreviewKind('error');
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    };
    loadPreview();
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
    // eslint-disable-next-line
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mr-3" />
        <span className="text-slate-600">Validating link…</span>
      </div>
    );
  }

  if (needsPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="w-full max-w-md" data-testid="shared-password-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 text-slate-800 font-semibold mb-2">
              <Lock className="w-5 h-5" /> Password required
            </div>
            <p className="text-sm text-slate-600 mb-4">
              The sender protected this link. Enter the password to view the document.
            </p>
            <form onSubmit={onPasswordSubmit} className="space-y-3">
              <Input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                data-testid="shared-password-input"
              />
              {error && <p className="text-xs text-red-600">{error}</p>}
              <Button type="submit" disabled={submitting} className="w-full" data-testid="shared-password-submit">
                {submitting ? 'Verifying…' : 'Unlock'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Card className="w-full max-w-md" data-testid="shared-error-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 text-red-600 font-semibold mb-2">
              <AlertTriangle className="w-5 h-5" /> Link unavailable
            </div>
            <p className="text-sm text-slate-700">{error}</p>
            <p className="text-xs text-slate-500 mt-4">
              The link may have expired, been revoked, or reached its access limit. Ask the sender to issue a new one.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { document: doc, share } = data;
  const expiresDate = share?.expires_at ? new Date(share.expires_at) : null;
  const expiresStr = expiresDate ? expiresDate.toLocaleString() : 'unknown';

  return (
    <div className="min-h-screen bg-slate-100" data-testid="shared-document-page">
      <header className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText className="w-6 h-6 text-blue-600" />
          <div>
            <h1 className="text-lg font-semibold text-slate-900" data-testid="shared-doc-title">{doc.title}</h1>
            <p className="text-xs text-slate-500">
              {doc.doc_type} · v{doc.current_version} ·
              {' '}<span className="inline-flex items-center gap-1">
                {share.permission === 'download'
                  ? (<><Download className="w-3 h-3" /> Download allowed</>)
                  : (<><Eye className="w-3 h-3" /> View only</>)}
              </span>
            </p>
          </div>
        </div>
        {share.permission === 'download' && (
          <Button onClick={handleDownload} data-testid="shared-download-btn">
            <Download className="w-4 h-4 mr-2" /> Download
          </Button>
        )}
      </header>

      <div className="px-6 py-2 bg-amber-50 border-b border-amber-200 text-xs flex items-center gap-2 text-amber-800">
        <Clock className="w-3.5 h-3.5" />
        <span data-testid="shared-expiry">This link expires on {expiresStr}</span>
        {share.max_accesses && (
          <span className="ml-2">· Accessed {share.accessed_count}/{share.max_accesses}</span>
        )}
        <span className="ml-auto inline-flex items-center gap-1 text-emerald-700">
          <ShieldCheck className="w-3.5 h-3.5" /> Secure tokenised link
        </span>
      </div>

      <main className="p-6">
        <Card>
          <CardContent className="p-0" data-testid="shared-viewer">
            {previewLoading ? (
              <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 220px)' }}>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3" />
                <span className="text-slate-600 text-sm">Loading preview…</span>
              </div>
            ) : previewKind === 'pdf' ? (
              <div style={{ height: 'calc(100vh - 200px)' }} data-testid="shared-pdf-viewer">
                <PDFViewer url={previewUrl} />
              </div>
            ) : previewKind === 'image' ? (
              <div className="flex justify-center p-4 bg-slate-50 overflow-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
                <img src={previewUrl} alt={doc.title} className="max-w-full h-auto" data-testid="shared-image-viewer" />
              </div>
            ) : previewKind === 'text' ? (
              <pre className="p-6 text-sm font-mono whitespace-pre-wrap break-words overflow-auto bg-white text-slate-900" style={{ maxHeight: 'calc(100vh - 200px)' }} data-testid="shared-text-viewer">
                {previewText}
              </pre>
            ) : previewKind === 'error' ? (
              <div className="p-12 text-center text-slate-500" data-testid="shared-preview-error">
                <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-amber-500" />
                <p>Unable to load the document preview.</p>
              </div>
            ) : (
              <div className="p-12 text-center" data-testid="shared-preview-unsupported">
                <FileText className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                <p className="text-slate-700 font-medium mb-1">Preview not available for this file type</p>
                <p className="text-sm text-slate-500 mb-5">Word, Excel and other binary formats can't be previewed in the browser.</p>
                {share.permission === 'download' && (
                  <Button onClick={handleDownload} data-testid="shared-unsupported-download">
                    <Download className="w-4 h-4 mr-2" /> Download to view
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
        <p className="text-center text-[11px] text-slate-400 mt-4">
          Powered by ReasonDMS · External secure share
        </p>
      </main>
    </div>
  );
};

export default SharedDocument;
